import { useEffect, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import ReviewCard from "../ReviewCard";
import WriteReviewForm from "../WriteReviewForm";

const StarIcon = ({ filled = false, size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill={filled ? "currentColor" : "#e6e6e6"}
  >
    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const ReviewsModal = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, average: 0 });
  const [summaryReady, setSummaryReady] = useState(false);

  const fetchReviews = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/reviews?limit=9`
      );
      if (response.data?.status) {
        const list = Array.isArray(response.data.reviews) ? response.data.reviews : [];
        setReviews(list);
        setSummary({
          total: Number(response.data.total ?? list.length) || 0,
          average: Number(response.data.average_rating ?? 0) || 0,
        });
      } else {
        setReviews([]);
        setSummary({ total: 0, average: 0 });
      }
    } catch {
      setReviews([]);
      setSummary({ total: 0, average: 0 });
    } finally {
      setSummaryReady(true);
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchReviews(true);
  }, [open]);

  const toggleOpen = (e) => {
    e.preventDefault();
    if (open) {
      setOpen(false);
      setView("list");
      return;
    }
    setView("list");
    setOpen(true);
  };

  return (
    <div className={`reviews_widget${open ? " is-open" : ""}`}>
      {open && (
        <div className="reviews_widget_panel" role="dialog" aria-labelledby="reviewsWidgetTitle">
          <div className="reviews_widget_header">
            {view === "write" && (
              <button
                type="button"
                className="reviews_back_btn"
                onClick={() => setView("list")}
                aria-label="Back to reviews"
              >
                ←
              </button>
            )}
            <span className="reviews_widget_badge">
              <StarIcon filled size={16} />
            </span>
            <div>
              <h2 id="reviewsWidgetTitle">
                {view === "write" ? "Write Your Review" : "Customer Reviews"}
              </h2>
              {view === "write" ? (
                <p>Share your ride experience with Orbi.</p>
              ) : (
                <div className="reviews_widget_summary">
                  <strong>{summary.average.toFixed(1)}</strong>
                  <span className="reviews_widget_summary_stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        filled={star <= Math.round(summary.average)}
                        size={12}
                      />
                    ))}
                  </span>
                  <span>
                    {summary.total} {summary.total === 1 ? "Review" : "Reviews"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="reviews_widget_content">
            {view === "list" ? (
              <>
                <div className="review_slide_card">
                  {loading && <p className="reviews_empty">Loading reviews...</p>}
                  {!loading && reviews.length === 0 && (
                    <p className="reviews_empty">
                      No reviews yet. Be the first to share your experience.
                    </p>
                  )}
                  {!loading && reviews.length > 0 && (
                    <Swiper
                      key={reviews.map((item) => item.id).join("-")}
                      modules={[Pagination, Autoplay]}
                      pagination={{ clickable: true }}
                      autoplay={
                        reviews.length > 1
                          ? {
                              delay: 3500,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                            }
                          : false
                      }
                      loop={reviews.length > 1}
                      spaceBetween={0}
                      slidesPerView={1}
                      grabCursor
                      className="reviews_card_swiper"
                    >
                      {reviews.map((item) => (
                        <SwiperSlide key={item.id}>
                          <ReviewCard item={item} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}
                </div>

                <button
                  type="button"
                  className="reviews_write_cta"
                  onClick={() => setView("write")}
                >
                  Write Your Review
                </button>
                <Link to="/reviews" className="reviews_view_all" onClick={() => setOpen(false)}>
                  View All Reviews
                </Link>
              </>
            ) : (
              <div className="review_slide_card write_review_card">
                <WriteReviewForm
                  onSuccess={() => {
                    setView("list");
                    fetchReviews(false);
                    window.dispatchEvent(new CustomEvent("orbi:review-submitted"));
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className={open ? "reviews_widget_close" : "reviews_fixed_btn"}
        onClick={toggleOpen}
        aria-label={
          open
            ? "Close reviews"
            : summaryReady
              ? `Open reviews, ${summary.average.toFixed(1)} average, ${summary.total} ${summary.total === 1 ? "review" : "reviews"}`
              : "Open reviews"
        }
      >
        {open ? (
          <span aria-hidden="true">×</span>
        ) : (
          <span className="reviews_fixed_btn_count">
            {summaryReady
              ? `${summary.average.toFixed(1)} ★ | ${summary.total} ${summary.total === 1 ? "Review" : "Reviews"}`
              : "Reviews"}
          </span>
        )}
      </button>
    </div>
  );
};

export default ReviewsModal;
